import { useState } from "react";
import { toast } from "sonner";
import { Star, PenLine, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const inputCls =
  "h-12 rounded-none border-white/15 bg-black/40 px-4 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue";

export const ReviewForm = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", vehicle: "", quote: "", rating: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      setDone(true);
      toast.success("Thanks! Your review will appear after approval.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-trx-blue/40 bg-card p-8 text-center" data-testid="review-confirmation">
        <CheckCircle2 className="mx-auto h-10 w-10 text-trx-blue" />
        <h3 className="mt-4 font-display text-2xl font-bold text-white">Thanks For The Review.</h3>
        <p className="mx-auto mt-3 max-w-sm text-sm text-zinc-400">
          It will appear on the site as soon as our team approves it.
        </p>
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="write-review-btn"
        className="flex items-center gap-3 border border-white/25 px-8 py-4 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:border-trx-blue hover:text-trx-blue"
      >
        <PenLine className="h-4 w-4" /> Write A Review
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-2xl border border-white/10 bg-card p-8" data-testid="review-form">
      <h3 className="font-display text-2xl font-bold tracking-tight text-white">Share Your Experience</h3>
      <div className="mt-5 flex gap-2" data-testid="review-rating">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setForm((f) => ({ ...f, rating: n }))}
            data-testid={`rating-star-${n}`}
            aria-label={`${n} stars`}
            className="transition-transform duration-200 hover:scale-110"
          >
            <Star className={`h-6 w-6 ${n <= form.rating ? "fill-trx-blue text-trx-blue" : "text-zinc-600"}`} />
          </button>
        ))}
      </div>
      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <Input required placeholder="Your name *" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} data-testid="review-name" />
        <Input placeholder="Vehicle / service (optional)" value={form.vehicle} onChange={(e) => setForm((f) => ({ ...f, vehicle: e.target.value }))} className={inputCls} data-testid="review-vehicle" />
      </div>
      <Textarea
        required
        placeholder="Tell others about your experience *"
        value={form.quote}
        onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
        className="mt-4 min-h-[110px] rounded-none border-white/15 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus-visible:ring-trx-blue"
        data-testid="review-quote"
      />
      <div className="mt-6 flex gap-3">
        <button
          type="submit"
          disabled={submitting}
          data-testid="review-submit"
          className="bg-trx-blue px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:bg-white hover:text-black disabled:opacity-50"
        >
          {submitting ? "Submitting…" : "Submit Review"}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="border border-white/20 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 hover:text-white">
          Cancel
        </button>
      </div>
    </form>
  );
};
